// apps/web/src/pages/admin/AdminSettings.tsx
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import {
  Shield,
  Globe,
  Database,
  Palette,
  Save,
  Loader2,
  Users,
  Settings,
  Bell,
  Lock,
  Eye,
} from 'lucide-react';
import type { Setting } from '@hc-alumni/shared';

type FieldType = 'text' | 'textarea' | 'number' | 'boolean';

interface SettingSchema {
  key: string;
  label: string;
  type: FieldType;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  group: 'basic' | 'footer' | 'feature' | 'media' | 'notify';
  defaultValue?: string;
}

const SETTINGS_SCHEMA: SettingSchema[] = [
  {
    key: 'site_name',
    label: '網站名稱',
    type: 'text',
    icon: Globe,
    group: 'basic',
    description: '導覽列、頁尾、瀏覽器標籤顯示的名稱',
    defaultValue: '和春技術學院校友會',
  },
  {
    key: 'site_description',
    label: '網站描述（SEO）',
    type: 'textarea',
    icon: Globe,
    group: 'basic',
    description: '搜尋結果與社群分享預覽用',
    defaultValue: '和春技術學院校友會官方網站 - 校友名片分享平台',
  },
  {
    key: 'site_keywords',
    label: 'SEO 關鍵字',
    type: 'text',
    icon: Globe,
    group: 'basic',
    description: '以逗號分隔，例如：和春技術學院,校友會,校友名片',
    defaultValue: '和春技術學院,校友會,校友名片,校友網',
  },
  {
    key: 'site_tagline',
    label: '頁尾簡介文字',
    type: 'textarea',
    icon: Globe,
    group: 'footer',
    description: '頁尾 LOGO 下方的短文',
    defaultValue: '凝聚校友情誼，傳承和春精神，共創美好未來。',
  },
  {
    key: 'show_contact_address',
    label: '顯示聯絡地址',
    type: 'boolean',
    icon: Globe,
    group: 'footer',
    description: '關閉後頁尾不顯示地址（文字仍可先填好，之後再開）',
    defaultValue: '0',
  },
  {
    key: 'contact_address',
    label: '聯絡地址',
    type: 'text',
    icon: Globe,
    group: 'footer',
    description: '頁尾「聯絡資訊」地址',
    defaultValue: '桃園市龜山區文化一路568號',
  },
  {
    key: 'show_contact_phone',
    label: '顯示聯絡電話',
    type: 'boolean',
    icon: Globe,
    group: 'footer',
    description: '關閉後頁尾不顯示電話（文字仍可先填好，之後再開）',
    defaultValue: '0',
  },
  {
    key: 'contact_phone',
    label: '聯絡電話',
    type: 'text',
    icon: Globe,
    group: 'footer',
    description: '頁尾顯示的電話',
    defaultValue: '(03) 328-1181',
  },
  {
    key: 'contact_website_label',
    label: '官網顯示文字',
    type: 'text',
    icon: Globe,
    group: 'footer',
    description: '例如 hc.edu.tw',
    defaultValue: 'hc.edu.tw',
  },
  {
    key: 'contact_website_url',
    label: '官網連結 URL',
    type: 'text',
    icon: Globe,
    group: 'footer',
    description: '完整網址，需含 https://',
    defaultValue: 'https://www.hc.edu.tw',
  },
  {
    key: 'footer_copyright',
    label: '版權名稱',
    type: 'text',
    icon: Globe,
    group: 'footer',
    description: '頁尾 © 後面顯示的名稱',
    defaultValue: '和春技術學院校友會',
  },
  {
    key: 'admin_verify_required',
    label: '需要管理員審核',
    type: 'boolean',
    icon: Shield,
    group: 'feature',
    description: '開啟後校友名片需審核通過才會顯示於公開名冊',
    defaultValue: '1',
  },
  {
    key: 'allow_public_directory',
    label: '允許公開瀏覽名冊',
    type: 'boolean',
    icon: Users,
    group: 'feature',
    description: '關閉後需登入才能查看校友名冊',
    defaultValue: '1',
  },
  {
    key: 'email_notification',
    label: '新註冊 Email 通知',
    type: 'boolean',
    icon: Bell,
    group: 'notify',
    description: '有新校友時發 Email（需另外接 Email 服務）',
    defaultValue: '0',
  },
  {
    key: 'telegram_notify_enabled',
    label: 'Telegram 待審推播',
    type: 'boolean',
    icon: Bell,
    group: 'notify',
    description: '校友提交名片待審時，推播到 Telegram',
    defaultValue: '1',
  },
  {
    key: 'telegram_chat_id',
    label: 'Telegram Chat ID',
    type: 'text',
    icon: Bell,
    group: 'notify',
    description: '群組或個人 chat_id（例如 -100xxxxxxxxxx）。也可只用 wrangler secret TELEGRAM_CHAT_ID',
    defaultValue: '',
  },
  {
    key: 'max_card_size_mb',
    label: '名片最大容量 (MB)',
    type: 'number',
    icon: Database,
    group: 'media',
    description: '原始上傳上限參考值；前端仍會自動壓縮',
    defaultValue: '5',
  },
  {
    key: 'allowed_image_types',
    label: '允許的圖片格式',
    type: 'text',
    icon: Database,
    group: 'media',
    description: '以逗號分隔，例如：image/jpeg,image/png,image/webp',
    defaultValue: 'image/jpeg,image/png,image/webp',
  },
  {
    key: 'session_days',
    label: '登入保持天數',
    type: 'number',
    icon: Lock,
    group: 'media',
    description: 'Cookie 過期天數，建議 30 天',
    defaultValue: '30',
  },
];

const GROUP_META: Record<
  SettingSchema['group'],
  { title: string; icon: React.ComponentType<{ className?: string }> }
> = {
  basic: { title: '基本資訊', icon: Globe },
  footer: { title: '頁尾與聯絡資訊', icon: Globe },
  feature: { title: '功能開關', icon: Shield },
  notify: { title: '通知設定', icon: Bell },
  media: { title: '上傳與登入', icon: Database },
};

function buildInitial(fromServer: Setting[]): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const s of SETTINGS_SCHEMA) {
    const found = fromServer.find((d) => d.key === s.key);
    const raw = found?.value;
    if (raw !== undefined && raw !== null && String(raw) !== '') {
      initial[s.key] = String(raw);
    } else {
      initial[s.key] = s.defaultValue ?? '';
    }
  }
  return initial;
}

export default function AdminSettings() {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [hydrated, setHydrated] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api.admin.getSettings(),
  });

  useEffect(() => {
    if (!data) return;
    setSettings(buildInitial(data as Setting[]));
    setHydrated(true);
  }, [data]);

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, string>) => {
      const results = await Promise.allSettled(
        SETTINGS_SCHEMA.map(async (schema) => {
          const value = payload[schema.key] ?? schema.defaultValue ?? '';
          await api.admin.saveSetting(schema.key, value);
          return schema.key;
        })
      );
      const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      if (failed.length > 0) {
        const msg = failed
          .map((f) => (f.reason instanceof Error ? f.reason.message : String(f.reason)))
          .join('；');
        throw new Error(msg || `有 ${failed.length} 項設定儲存失敗`);
      }
    },
    onSuccess: () => {
      toast.success('設定已全部儲存');
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      // 前台頁尾／站名立即重抓
      queryClient.invalidateQueries({ queryKey: ['public-settings'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : '儲存失敗'),
  });

  if (isLoading || !hydrated) {
    return (
      <div className="container-custom py-12 text-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-alumni-600" />
        載入設定中…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-custom py-12 text-center text-red-600">
        無法載入設定：{error instanceof Error ? error.message : '請重新登入後再試'}
      </div>
    );
  }

  const groups: SettingSchema['group'][] = ['basic', 'footer', 'feature', 'notify', 'media'];

  return (
    <div className="container-custom">
      <div className="mb-8 animate-fade-in">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">系統設定</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          管理網站基本設定、通知、上傳限制與功能開關
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {groups.map((groupKey) => {
            const meta = GROUP_META[groupKey];
            const Icon = meta.icon;
            const fields = SETTINGS_SCHEMA.filter((s) => s.group === groupKey);
            return (
              <section key={groupKey} className="card animate-fade-in">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                    <Icon className="w-5 h-5 text-alumni-600" />
                    {meta.title}
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  {fields.map((setting) =>
                    setting.type === 'boolean' ? (
                      <ToggleField
                        key={setting.key}
                        setting={setting}
                        checked={
                          settings[setting.key] === '1' || settings[setting.key] === 'true'
                        }
                        onChange={(checked) =>
                          handleChange(setting.key, checked ? '1' : '0')
                        }
                      />
                    ) : (
                      <SettingField
                        key={setting.key}
                        setting={setting}
                        value={settings[setting.key] ?? ''}
                        onChange={handleChange}
                      />
                    )
                  )}
                </div>
              </section>
            );
          })}
        </div>

        <div className="lg:col-span-1">
          <div className="card sticky top-24">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <Settings className="w-5 h-5 text-alumni-600" />
                儲存設定
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <button
                type="button"
                onClick={() => saveMutation.mutate(settings)}
                disabled={saveMutation.isPending}
                className="btn-primary w-full py-3 text-lg"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> 儲存中…
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" /> 儲存所有設定
                  </>
                )}
              </button>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 space-y-2">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">快速操作</h4>
                <button
                  type="button"
                  className="btn-secondary w-full justify-start"
                  onClick={async () => {
                    try {
                      await api.admin.testTelegram();
                      toast.success('Telegram 測試推播已送出');
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : 'Telegram 測試失敗');
                    }
                  }}
                >
                  <Bell className="w-4 h-4 mr-2" /> 發送 Telegram 測試推播
                </button>
                <button
                  type="button"
                  className="btn-secondary w-full justify-start"
                  onClick={() => window.open('/', '_blank')}
                >
                  <Eye className="w-4 h-4 mr-2" /> 預覽首頁
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                儲存會寫入資料庫，立即生效。Bot Token 仍請用 wrangler secret 管理。
              </p>
            </div>
          </div>

          <div className="mt-6 card bg-alumni-50 dark:bg-alumni-950/40 border border-alumni-100 dark:border-alumni-900">
            <div className="p-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-alumni-800 dark:text-alumni-200 mb-3">
                <Palette className="w-5 h-5" />
                關於主題色彩
              </h3>
              <p className="text-sm text-alumni-800/90 dark:text-alumni-200/90 leading-relaxed">
                主色對齊校徽藍 <code className="rounded bg-white/70 dark:bg-black/30 px-1.5 py-0.5 text-xs font-mono">#1B3F8B</code>
                ，搭配 2026 冷色系介面。若需微調，請修改
                <code className="rounded bg-white/70 dark:bg-black/30 px-1.5 py-0.5 text-xs font-mono">tailwind.config.js</code>
                後重新部署。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingField({
  setting,
  value,
  onChange,
}: {
  setting: SettingSchema;
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const { key, label, type, icon: Icon, description } = setting;
  return (
    <div>
      <label className="label flex items-center gap-2" htmlFor={key}>
        <Icon className="w-5 h-5 text-gray-400" />
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={key}
          value={value}
          onChange={(e) => onChange(key, e.target.value)}
          rows={3}
          className="input"
        />
      ) : (
        <input
          id={key}
          type={type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(key, e.target.value)}
          className="input"
        />
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">{description}</p>
    </div>
  );
}

function ToggleField({
  setting,
  checked,
  onChange,
}: {
  setting: SettingSchema;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { label, icon: Icon, description } = setting;
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
      <Icon className="w-6 h-6 text-alumni-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{label}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
          </div>
          <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-alumni-600 focus:ring-offset-2 ${
              checked ? 'bg-alumni-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            role="switch"
            aria-checked={checked}
            aria-label={label}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                checked ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
